import React, { useState } from "react";
import { Send, X, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";

/**
 * Phase 19p — "Send to Recipient" modal.
 *
 * Posts to ``POST /api/projects/{pid}/handover/email`` with the bundle
 * scope (stage / project) and recipient details. Resend handles delivery.
 *
 * Props:
 *   projectId       — string
 *   scope           — "stage" | "project"
 *   stageCode       — string (required when scope == "stage")
 *   defaultLabel    — display label for the bundle (e.g. "Stage B.2 handover")
 *   onClose         — () => void
 */
export default function SendHandoverModal({ projectId, scope, stageCode, defaultLabel, onClose }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null); // { id, size_bytes, attached, to }

  const handleSend = async (e) => {
    e.preventDefault();
    if (!to.trim()) { setErr("Please enter a recipient email."); return; }
    setBusy(true); setErr("");
    try {
      const ccList = cc.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post(`/projects/${projectId}/handover/email`, {
        to: to.trim(),
        cc: ccList.length ? ccList : null,
        subject: subject.trim() || null,
        message: message.trim() || null,
        scope,
        stage_code: scope === "stage" ? stageCode : null,
      });
      setDone(data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(8,10,15,0.78)",
        zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, backdropFilter: "blur(4px)",
      }}
      data-testid="send-handover-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <form
        onSubmit={handleSend}
        style={{
          width: "min(560px, 100%)", background: "var(--pf-surface)",
          border: "1px solid var(--pf-border)", borderRadius: 8,
          padding: 24, boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="pf-overline" style={{ color: "var(--pf-spark)" }}>SEND TO RECIPIENT</div>
            <h2 className="pf-heading" style={{ fontSize: 18, margin: "4px 0 0" }}>
              Email <strong>{defaultLabel}</strong>
            </h2>
            <p style={{ fontSize: 12, color: "var(--pf-text-muted)", marginTop: 6 }}>
              The handover ZIP (Markdown · PDF · Word · Excel · SVG) will be attached.
              Files over 40 MB include a download link instead.
            </p>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}
                  data-testid="send-handover-close" style={{ padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div style={{ padding: "20px 16px", background: "color-mix(in srgb, #10B981 12%, transparent)",
                        border: "1px solid #10B981", borderRadius: 6, marginBottom: 16 }}
               data-testid="send-handover-success">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <CheckCircle2 size={18} color="#10B981" />
              <strong style={{ color: "#10B981" }}>Email sent successfully</strong>
            </div>
            <div style={{ fontSize: 13, color: "var(--pf-text)" }}>
              Bundle delivered to <strong>{done.to}</strong> ({(done.size_bytes / 1024 / 1024).toFixed(1)} MB
              {done.attached ? " · attached" : " · download link only (over 40 MB cap)"}).
            </div>
            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-text-muted)", marginTop: 6 }}>
              Resend ID: {done.id}
            </div>
            <div style={{ marginTop: 14 }}>
              <button type="button" className="btn btn-primary" onClick={onClose} data-testid="send-handover-done">
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <label className="pf-overline" style={{ display: "block", marginBottom: 4 }}>RECIPIENT EMAIL *</label>
            <input
              type="email" required value={to} onChange={(e) => setTo(e.target.value)} disabled={busy}
              placeholder="client@company.com"
              data-testid="send-handover-to"
              className="pf-input" style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="pf-overline" style={{ display: "block", marginBottom: 4 }}>CC (comma-separated)</label>
            <input
              type="text" value={cc} onChange={(e) => setCc(e.target.value)} disabled={busy}
              placeholder="optional, e.g. you@yourdomain.com"
              data-testid="send-handover-cc"
              className="pf-input" style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="pf-overline" style={{ display: "block", marginBottom: 4 }}>SUBJECT</label>
            <input
              type="text" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy}
              placeholder={`Capexara Process Industry — ${defaultLabel}`}
              data-testid="send-handover-subject"
              className="pf-input" style={{ width: "100%", marginBottom: 12 }}
            />

            <label className="pf-overline" style={{ display: "block", marginBottom: 4 }}>PERSONAL MESSAGE (optional)</label>
            <textarea
              value={message} onChange={(e) => setMessage(e.target.value)} disabled={busy}
              placeholder="Add a short note for the recipient (e.g. context, deadline, reviewer instructions)…"
              rows={4}
              data-testid="send-handover-message"
              className="pf-input" style={{ width: "100%", marginBottom: 16, fontFamily: "inherit" }}
            />

            {err && (
              <div style={{ padding: "8px 12px", background: "color-mix(in srgb, var(--pf-err) 12%, transparent)",
                            border: "1px solid var(--pf-err)", borderRadius: 4, marginBottom: 12, fontSize: 12.5,
                            color: "var(--pf-err)", display: "flex", alignItems: "center", gap: 6 }}
                   data-testid="send-handover-error">
                <AlertTriangle size={14} /> {err}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}
                      data-testid="send-handover-cancel">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || !to.trim()}
                      data-testid="send-handover-submit">
                {busy
                  ? <><Loader2 size={14} className="pf-spin"/> Sending…</>
                  : <><Send size={14}/> Send handover</>}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
